import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { CheckCircle2, XCircle, RefreshCw, Loader2, ShieldCheck, Clock } from "lucide-react";

const fmtWhen = (iso) => { try { return new Date(iso).toLocaleString("en-IN", { day: "numeric", month: "short", year: "numeric", hour: "numeric", minute: "2-digit" }); } catch { return iso || "—"; } };

/**
 * AdminReinstatementInbox — dedicated admin tab surfacing every pending
 * reinstatement request filed from the Access Suspended page.  Each row
 * shows the partner's business, contact, case ref, submitted date, and the
 * verbatim note.  Approve → auto-reactivates + emails; Reject → prompts for
 * an optional rationale and emails the partner.
 */
export function AdminReinstatementInbox({ api }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState("");
  const [status, setStatus] = useState("pending");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get(`/admin/reinstatement-requests?status=${status}`);
      setRows(r.data?.requests || []);
    } catch { toast.error("Could not load requests"); }
    finally { setLoading(false); }
  }, [api, status]);
  useEffect(() => { load(); }, [load]);

  const approve = async (id) => {
    if (!window.confirm("Approve reinstatement? The partner will be reactivated and receive a restoration email.")) return;
    setBusy(id);
    try { await api.post(`/admin/reinstatement-requests/${id}/approve`); toast.success("Approved · partner reactivated"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Approve failed"); }
    finally { setBusy(""); }
  };
  const reject = async (id) => {
    const reason = window.prompt("Reject reinstatement — optional note (sent verbatim to the partner):", "");
    if (reason === null) return;
    setBusy(id);
    try { await api.post(`/admin/reinstatement-requests/${id}/reject`, { reason: reason.trim() }); toast.success("Rejected · partner notified"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Reject failed"); }
    finally { setBusy(""); }
  };

  return (
    <div className="pt-2" data-testid="reinstatement-inbox">
      <div className="flex flex-wrap items-end justify-between gap-4 pb-6 mb-6 border-b border-white/10">
        <div>
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-sm bg-[#0e0e0e] border border-[#D8B23E]/40 flex items-center justify-center">
              <ShieldCheck size={18} className="text-[#D8B23E]" />
            </div>
            <div>
              <h2 className="font-serif text-3xl md:text-4xl">Reinstatement Requests</h2>
              <p className="font-body text-xs text-[#8A8A82] mt-1">Formal reinstatement pleas from deactivated partners.</p>
            </div>
          </div>
        </div>
        <div className="flex gap-2">
          {["pending", "approved", "rejected"].map((s) => (
            <button
              key={s}
              data-testid={`reinstatement-filter-${s}`}
              onClick={() => setStatus(s)}
              className={`rounded-full px-4 py-1.5 font-grotesk text-[10px] uppercase tracking-[0.22em] border ${status === s ? "bg-[#D8B23E] text-black border-[#D8B23E]" : "border-white/15 text-[#C9C9C2] hover:border-[#D8B23E]"}`}
            >{s}</button>
          ))}
          <button onClick={load} className="text-[#8A8A82] hover:text-[#B87333] px-2"><RefreshCw size={14}/></button>
        </div>
      </div>

      {loading ? (
        <p className="font-body text-[#8A8A82]">Loading…</p>
      ) : rows.length === 0 ? (
        <div className="border border-white/10 rounded-sm p-10 text-center">
          <Clock className="mx-auto text-[#8A8A82] mb-3" size={26} />
          <p className="font-body text-sm text-[#8A8A82]">No {status} reinstatement requests.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {rows.map((r) => (
            <motion.div
              key={r.id}
              initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }}
              className="border border-white/10 rounded-sm p-5 hover:border-[#D8B23E]/40 transition-colors"
              data-testid={`reinstatement-row-${r.id}`}
            >
              <div className="flex flex-wrap items-start justify-between gap-4">
                <div className="min-w-0">
                  <div className="font-serif text-xl truncate">{r.business_name || r.user_email}</div>
                  <div className="font-body text-xs text-[#8A8A82] mt-0.5">
                    {r.user_email} · Submitted {fmtWhen(r.submitted_at)}
                  </div>
                  <div className="font-mono text-[11px] text-[#D8B23E] mt-1">{r.case_ref}</div>
                </div>
                <span className="font-grotesk text-[10px] uppercase tracking-[0.22em] px-2.5 py-1 rounded-full border"
                        style={{
                          color: r.status === "approved" ? "#2FB999" : r.status === "rejected" ? "#C0392B" : "#D8B23E",
                          borderColor: (r.status === "approved" ? "#2FB999" : r.status === "rejected" ? "#C0392B" : "#D8B23E") + "55",
                        }}>
                  {r.status}
                </span>
              </div>
              {r.note && (
                <div className="mt-4 border-l-2 border-[#D8B23E] pl-4 py-1 text-[13px] text-[#C9C9C2] italic">"{r.note}"</div>
              )}
              {r.rejection_reason && (
                <div className="mt-3 border-l-2 border-[#C0392B] pl-4 py-1 text-[12px] text-[#C0392B]">Rejected: {r.rejection_reason}</div>
              )}
              {r.status === "pending" && (
                <div className="mt-4 flex flex-wrap gap-2 pt-4 border-t border-white/[0.06]">
                  <button
                    data-testid={`reinstatement-approve-${r.id}`}
                    disabled={busy === r.id}
                    onClick={() => approve(r.id)}
                    className="inline-flex items-center gap-2 rounded-full bg-[#2FB999] text-black px-5 py-2 font-grotesk text-[10px] uppercase tracking-[0.22em] hover:opacity-90 disabled:opacity-50"
                  >{busy === r.id ? <Loader2 size={12} className="animate-spin"/> : <CheckCircle2 size={12}/>} Approve & Reactivate</button>
                  <button
                    data-testid={`reinstatement-reject-${r.id}`}
                    disabled={busy === r.id}
                    onClick={() => reject(r.id)}
                    className="inline-flex items-center gap-2 rounded-full border border-[#C0392B]/40 text-[#C0392B] hover:bg-[#C0392B] hover:text-white px-5 py-2 font-grotesk text-[10px] uppercase tracking-[0.22em] disabled:opacity-50"
                  ><XCircle size={12}/> Reject</button>
                </div>
              )}
            </motion.div>
          ))}
        </div>
      )}
    </div>
  );
}
